import { Injectable, ChangeDetectorRef } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { Order } from '../../../../shared/objects/order/order';
import { AuthService } from '../../../../core/services/auth.service';

import { CostType } from '../../../../shared/objects/order/costType';
import { Currency } from '../../../../shared/objects/order/currency';
import { Area } from '../../../../shared/objects/order/area';
import { AvailableTimeslot } from '../../../../shared/objects/order/availableTimeslot';
import { TransportInformation } from '../../../../shared/objects/transport-information/transport-information';

import moment from 'moment';
import { Constants } from '../../../../shared/const/constants';
import { RejectedByDetails } from '../../../../shared/objects/order/rejectedByDetails';
import { OrderStatus } from '../../../../shared/objects/enum/orderStatus';
import { OrderDocumentService } from './order-document.service';
import { Formatting } from '../../../../shared/utils/formatting.utils';
import { ProductInformation } from '../../../../shared/objects/transport-information/product-information';
import { TruckType } from '../../../../shared/objects/enum/truckType';
import { TransportInfoContainer } from '../../../../shared/objects/transport-information/transport-information-container';
import { Country } from '../../../../shared/objects/order/country';
import { TimeslotType } from '../../../../shared/objects/enum/timeslotType';

@Injectable()
export class OrderService {
  private currencies: Currency[];

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private orderDocumentService: OrderDocumentService,
  ) {
    this.getCurrenciesLocally();
  }

  // TODO: Separate in multiple services by business objects
  getAvailableCostTypesInPortal(): Observable<CostType[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<CostType[]>(environment.costTypeServiceUrl, {
      headers,
    });
  }

  getAllCostTypes(): Observable<CostType[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<CostType[]>(
      `${environment.costTypeServiceUrl}/all`,
      {
        headers,
      },
    );
  }

  getCurrencies(): Observable<Currency[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Currency[]>(environment.currencyServiceUrl, {
      headers,
    });
  }

  getOrder(orderId: string): Observable<Order> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Order>(
      `${environment.orderServiceUrl}/${orderId}`,
      { headers },
    );
  }

  orderExists(orderId: string): Observable<Boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Boolean>(
      `${environment.orderServiceUrl}/exists/${orderId}`,
      { headers },
    );
  }

  saveLoadingActuals(order: Order): Observable<Object> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put(
      `${environment.orderServiceUrl}/${order.id}/loadingActuals`,
      order,
      { headers },
    );
  }

  saveDeliveryActuals(order: Order): Observable<Object> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put(
      `${environment.orderServiceUrl}/${order.id}/deliveryActuals`,
      order,
      { headers },
    );
  }

  saveTimeslot(order: Order, timeslotType: TimeslotType): Observable<Object> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put(
      `${environment.orderServiceUrl}/${order.id}/timeslot/${timeslotType}`,
      order,
      { headers },
    );
  }

  acceptOrder(orderId: number): Promise<number[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient
      .get<
        number[]
      >(`${environment.orderConfirmationServiceUrl}/${orderId}/acceptOrder`, { headers })
      .toPromise();
  }

  denyOrder(orderId: number): Promise<number[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient
      .get<
        number[]
      >(`${environment.orderConfirmationServiceUrl}/${orderId}/denyOrder`, { headers })
      .toPromise();
  }

  getAreas(orderId: string, loadingAddressId: number): Observable<Area[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<Area[]>(
      `${environment.areaServiceUrl}/byaddressid/${orderId}/${loadingAddressId}`,
      { headers },
    );
  }

  getAvailableTimeslots(
    areaId: number,
    date: any,
    orderId: number,
  ): Observable<AvailableTimeslot[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<AvailableTimeslot[]>(
      `${environment.areaServiceUrl}/${areaId}/timeslots/${date}/${orderId}`,
      { headers },
    );
  }

  postTransportInformation(
    transportInformation: TransportInformation,
  ): Observable<TransportInformation> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put<TransportInformation>(
      `${environment.orderServiceUrl}/TransportInformation/${transportInformation.orderId}`,
      transportInformation,
      { headers },
    );
  }

  getCountries(): Promise<Country[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient
      .get<Country[]>(`${environment.orderServiceUrl}/getCountryPrefixes`, {
        headers,
      })
      .toPromise();
  }

  getDossierOrdersBy(dossierId: number): Promise<number[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient
      .get<
        number[]
      >(`${environment.orderServiceUrl}/getDossierOrders/${dossierId}`, { headers })
      .toPromise();
  }

  // TODO: Remove the currencies functions from the order service and use the currency service instead
  orderCurrencySymbol(currencyId: number): string {
    if (!currencyId || !this.currencies || this.currencies.length === 0) {
      return '';
    }

    const currency = this.currencies.find(c => c.currencyId === currencyId);
    return !!currency ? currency.symbol : '';
  }

  orderCurrency(order: any): Currency {
    if (
      !order ||
      !order.currencyId ||
      !this.currencies ||
      this.currencies.length === 0
    ) {
      return null;
    }

    const currency = this.currencies.find(
      c => c.currencyId === order.currencyId,
    );
    return !!currency ? currency : null;
  }

  private getCurrenciesLocally() {
    this.getCurrencies().subscribe(currencies => {
      this.currencies = currencies;
    });
  }

  getRejectedByDetails(orderId: number): Observable<RejectedByDetails> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<RejectedByDetails>(
      `${environment.orderConfirmationServiceUrl}/getRejectedByDetails/${orderId}`,
      { headers },
    );
  }

  setupOrdersDetails(
    orders: Order[],
    changeDetector: ChangeDetectorRef,
  ): Order[] {
    orders.forEach(order => {
      order.documentStatus =
        this.orderDocumentService.getOrderDocumentStatus(order);
      if (order.status === OrderStatus.CarrierDenied.toString()) {
        this.getRejectedByDetails(+order.orderId).subscribe(details => {
          order.rejectedByDetails = details;
          if (!!changeDetector) {
            changeDetector.markForCheck();
          }
        });
      }
    });

    return orders;
  }

  getFilteredOrders(filters): Observable<Order[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.put<Order[]>(
      `${environment.orderServiceUrl}/filterOrders`,
      filters,
      { headers },
    );
  }

  getContainer(
    license: string,
    type: number,
    transport: number,
  ): Observable<TransportInfoContainer> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<TransportInfoContainer>(
      `${environment.orderServiceUrl}/getContainer/`,
      {
        headers,
        params: {
          license: license,
          type: type.toString(),
          transport: transport.toString(),
        },
      },
    );
  }

  getBookingPeriodExpired(
    orderId: number,
    timeslotType: TimeslotType,
    areaId: number,
    date: moment.Moment,
  ): Observable<boolean> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<boolean>(
      `${environment.areaServiceUrl}/validateBookingPeriod/${orderId}/${timeslotType}/${areaId}/${date}`,
      { headers },
    );
  }
}
